// src/pages/TicketDetail.tsx
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "@/lib/auth";
import {
  subscribeToTicket, subscribeToMessages, addMessage, resolveTicket,
  uploadTicketAttachment, getAgents, assignTicket, getKBArticles,
  getActiveUsers, changeSubmitter, getConfigDoc,
} from "@/lib/firestore";
import { getAgentSuggestion } from "@/lib/gemini";
import { DEFAULT_MAX_INFO_ROUNDS } from "@/lib/integrations";
import { searchKBArticles } from "@/lib/kbSearch";
import { detectPlatform } from "@/lib/platform";
import { businessMsBetween, formatSlaDuration } from "@/lib/businessHours";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { BusinessSchedule, HelpDeskUser, KBArticle, SlaConfig, Ticket, TicketAttachment, TicketMessage } from "@/types";
import { StatusBadge, PriorityBadge, Avatar, Spinner, toast } from "@/components/shared";

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { helpDeskUser, firebaseUser } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  useDocumentTitle(ticket ? `${ticket.title} (${id?.slice(0, 8).toUpperCase()})` : "Ticket");
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showSubmitter, setShowSubmitter] = useState(false);
  const [submitterSearch, setSubmitterSearch] = useState("");
  const [agents, setAgents] = useState<HelpDeskUser[]>([]);
  const [users, setUsers] = useState<HelpDeskUser[]>([]);
  const [kbArticles, setKbArticles] = useState<KBArticle[]>([]);
  const [schedule, setSchedule] = useState<BusinessSchedule | undefined>(undefined);

  useEffect(() => {
    getKBArticles().then(setKbArticles).catch(() => { /* empty KB is fine */ });
    // Pull the SLA config so the countdown can show business minutes
    // when the helpdesk has a schedule set. Falls back to wall-clock.
    getConfigDoc<SlaConfig>("sla")
      .then((cfg) => setSchedule(cfg?.businessSchedule))
      .catch(() => { /* fall back to wall-clock */ });
  }, []);
  const messagesEnd = useRef<HTMLDivElement>(null);
  const replyFileInput = useRef<HTMLInputElement>(null);
  const assignRef = useRef<HTMLDivElement>(null);
  const submitterRef = useRef<HTMLDivElement>(null);
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";

  useEffect(() => {
    if (!showAssign) return;
    if (agents.length === 0) getAgents().then(setAgents);
    const onClick = (e: MouseEvent) => {
      if (assignRef.current && !assignRef.current.contains(e.target as Node)) setShowAssign(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssign]);

  useEffect(() => {
    if (!showSubmitter) return;
    if (users.length === 0) getActiveUsers().then(setUsers);
    const onClick = (e: MouseEvent) => {
      if (submitterRef.current && !submitterRef.current.contains(e.target as Node)) {
        setShowSubmitter(false);
        setSubmitterSearch("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmitter]);

  useEffect(() => {
    if (!id) return;
    const u1 = subscribeToTicket(id, setTicket);
    const u2 = subscribeToMessages(id, (msgs) => {
      setMessages(msgs);
      setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    return () => { u1(); u2(); };
  }, [id]);

  const handleSend = async () => {
    if ((!reply.trim() && replyFiles.length === 0) || !id) return;
    setSending(true);
    try {
      const uploaded: TicketAttachment[] = [];
      for (const file of replyFiles) {
        uploaded.push(await uploadTicketAttachment(id, file, firebaseUser!.uid));
      }
      await addMessage(id, {
        authorId: firebaseUser!.uid,
        authorName: helpDeskUser?.displayName ?? "User",
        authorRole: helpDeskUser?.role ?? "user",
        body: reply.trim(),
        attachments: uploaded,
      });
      setReply("");
      setReplyFiles([]);
      toast("Reply sent");
    } catch {
      toast("Failed to send reply", "error");
    }
    setSending(false);
  };

  const handleAssign = async (agent: HelpDeskUser | null) => {
    if (!id) return;
    try {
      await assignTicket(id, agent ? { uid: agent.uid, displayName: agent.displayName } : null);
      toast(agent ? `Assigned to ${agent.displayName}` : "Unassigned");
    } catch {
      toast("Failed to update assignment", "error");
    }
    setShowAssign(false);
  };

  const handleChangeSubmitter = async (user: HelpDeskUser) => {
    if (!id) return;
    try {
      await changeSubmitter(id, { uid: user.uid, email: user.email, displayName: user.displayName });
      toast(`Submitter changed to ${user.displayName || user.email}`);
    } catch {
      toast("Failed to change submitter", "error");
    }
    setShowSubmitter(false);
    setSubmitterSearch("");
  };

  const handleAISuggest = async () => {
    if (!ticket) return;
    setSuggesting(true);
    try {
      const context = `Ticket: ${ticket.title}\nDescription: ${ticket.description}\nCategory: ${ticket.category}\nPriority: ${ticket.priority}\nSuggest the next troubleshooting step as the IT agent.`;
      const suggestion = await getAgentSuggestion(context);
      setReply(suggestion);
    } catch {
      toast("Could not get AI suggestion", "error");
    }
    setSuggesting(false);
  };

  const handleResolve = async () => {
    if (!id) return;
    setResolving(true);
    await resolveTicket(id);
    toast("Ticket resolved — CSAT survey sent");
    setResolving(false);
  };

  if (!ticket) return <div style={{ padding: 20, color: "var(--text2)" }}>Loading ticket…</div>;

  // SLA countdown — counts business minutes if a schedule is configured,
  // otherwise wall-clock minutes. After breach, shows how long ago.
  const slaMs = ticket.slaBreach?.toMillis();
  const now = Date.now();
  const breached = slaMs !== undefined && now >= slaMs;
  const slaRemainingMs = slaMs === undefined
    ? null
    : breached
      ? -businessMsBetween(new Date(slaMs), new Date(now), schedule)
      : businessMsBetween(new Date(now), new Date(slaMs), schedule);
  const slaWarning = slaRemainingMs !== null && !breached && slaRemainingMs < 60 * 60 * 1000;

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Ticket header */}
        <div style={{ padding: "12px 18px", borderBottom: "0.5px solid var(--border)", background: "var(--surface)", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>← Back</button>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text2)", background: "var(--gray)", padding: "2px 6px", borderRadius: 4 }}>
            {ticket.id.slice(0, 8).toUpperCase()}
          </span>
          <StatusBadge status={ticket.status} />
          {ticket.priority && <PriorityBadge priority={ticket.priority} />}
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            {isAgent && (
              <>
                <button className="btn btn-gem btn-sm" onClick={handleAISuggest} disabled={suggesting}>
                  {suggesting ? <Spinner size={12} color="var(--gem)" /> : null}
                  AI Suggest
                </button>
                <button
                  className="btn btn-green btn-sm" onClick={handleResolve} disabled={resolving}
                  style={{ display: ticket.status === "resolved" ? "none" : undefined }}
                >
                  {resolving ? <Spinner size={12} color="var(--green-t)" /> : "✓ Resolve"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Conversation */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
          <h2 style={{ marginBottom: 4 }}>{ticket.title}</h2>
          <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--text2)", marginBottom: 14, flexWrap: "wrap" }}>
            <span>{ticket.submitterName ?? ticket.submitterEmail}</span>
            <span>·</span>
            <span>{ticket.category || "Uncategorized"}</span>
            {ticket.priority && <><span>·</span><span style={{ color: ticket.priority === "P1" ? "var(--red-t)" : "var(--text)", fontWeight: 500 }}>{ticket.priority}</span></>}
            <span>·</span>
            <span>{formatDistanceToNow(ticket.createdAt.toDate(), { addSuffix: true })}</span>
          </div>

          {/* Description */}
          <div className="card card-body" style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 8 }}>Description</div>
            <div className="md" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{ticket.description}</ReactMarkdown>
            </div>
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ticket.attachments.map((a, i) => <AttachmentChip key={i} att={a} />)}
              </div>
            )}
            {ticket.triagedAt && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "0.5px solid var(--border)", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span className="badge badge-gem">✓ AI Triaged</span>
                {ticket.triageReason && <span style={{ fontSize: 12, color: "var(--text2)", alignSelf: "center" }}>{ticket.triageReason}</span>}
              </div>
            )}
          </div>

          {/* Timeline */}
          {messages.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {messages.map((msg, i) => (
                <div key={msg.id} style={{ display: "flex", gap: 10, position: "relative" }}>
                  {i < messages.length - 1 && (
                    <div style={{ position: "absolute", left: 14, top: 30, width: 1, height: "calc(100% - 8px)", background: "var(--border)" }} />
                  )}
                  <Avatar name={msg.authorName} size={28} />
                  <div style={{ flex: 1, paddingBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {msg.authorName}
                      <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 6, fontWeight: 400 }}>
                        {msg.createdAt ? formatDistanceToNow(msg.createdAt.toDate(), { addSuffix: true }) : ""}
                      </span>
                    </div>
                    <div className="md" style={{
                      fontSize: 13, lineHeight: 1.55, marginTop: 4, padding: "9px 11px",
                      borderRadius: msg.authorRole === "user" ? "0 9px 9px 9px" : msg.authorRole === "ai" ? "9px 9px 9px 0" : "9px 0 9px 9px",
                      background: msg.authorRole === "agent" || msg.authorRole === "admin" ? "var(--blue-l)" : msg.authorRole === "ai" ? "var(--gem-l)" : "var(--gray)",
                      color: msg.authorRole === "agent" || msg.authorRole === "admin" ? "var(--blue-t)" : msg.authorRole === "ai" ? "var(--gem-t)" : "var(--text)",
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.body}</ReactMarkdown>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {msg.attachments.map((a, k) => <AttachmentChip key={k} att={a} />)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEnd} />
            </div>
          )}
        </div>

        {/* Reply box */}
        {ticket.status !== "resolved" && ticket.status !== "closed" && (
          <div style={{ borderTop: "0.5px solid var(--border)", padding: 12, background: "var(--surface)", flexShrink: 0 }}>
            <textarea
              className="fi" style={{ minHeight: 130, maxHeight: 360, resize: "vertical", marginBottom: 8, fontFamily: "var(--font)", lineHeight: 1.5 }}
              placeholder="Add a reply… (Shift+Enter for new line)"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            {replyFiles.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                {replyFiles.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, padding: "4px 8px", background: "var(--gray)", borderRadius: 4 }}>
                    📎 {f.name}
                    <button onClick={() => setReplyFiles(replyFiles.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red-t)", fontSize: 12, padding: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending || (!reply.trim() && replyFiles.length === 0)}>
                {sending ? <Spinner size={12} color="white" /> : null} Send Reply
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => replyFileInput.current?.click()}>📎 Attach</button>
              <input ref={replyFileInput} type="file" multiple style={{ display: "none" }}
                onChange={(e) => {
                  const fs = Array.from(e.target.files ?? []).filter((f) => {
                    if (f.size > 25 * 1024 * 1024) { toast(`${f.name} exceeds 25MB`, "error"); return false; }
                    return true;
                  });
                  setReplyFiles((prev) => [...prev, ...fs]);
                  e.target.value = "";
                }} />
              {isAgent && (
                <button className="btn btn-gem btn-sm" onClick={handleAISuggest} disabled={suggesting}>
                  {suggesting ? <Spinner size={12} color="var(--gem)" /> : "AI Suggest"}
                </button>
              )}
              {isAgent && (
                <button className="btn btn-green btn-sm" style={{ marginLeft: "auto" }} onClick={handleResolve}>
                  Mark Resolved ✓
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <div style={{ width: 240, borderLeft: "0.5px solid var(--border)", overflowY: "auto", flexShrink: 0, background: "var(--surface)" }}>
        {/* Details */}
        <div style={{ padding: 12, borderBottom: "0.5px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 8 }}>Details</div>
          {[
            { label: "Status",   val: <StatusBadge status={ticket.status} /> },
            { label: "Priority", val: ticket.priority ? <PriorityBadge priority={ticket.priority} /> : "Triaging…" },
            { label: "Category", val: ticket.category || "—" },
          ].map(({ label, val }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
              <span style={{ color: "var(--text2)" }}>{label}</span>
              <span style={{ fontWeight: 500 }}>{val}</span>
            </div>
          ))}
          <div ref={submitterRef} style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
            <span style={{ color: "var(--text2)" }}>Submitter</span>
            {isAgent ? (
              <button
                onClick={() => setShowSubmitter(!showSubmitter)}
                style={{ background: "var(--gray)", border: "none", padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "var(--font)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={ticket.submitterEmail}
              >
                {ticket.submitterName ?? ticket.submitterEmail} ▾
              </button>
            ) : (
              <span style={{ fontWeight: 500 }}>{ticket.submitterName ?? ticket.submitterEmail}</span>
            )}
            {showSubmitter && (
              <div className="card" style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, width: 240,
                maxHeight: 320, overflow: "auto", boxShadow: "var(--shadow-md)", zIndex: 30,
              }}>
                <div style={{ padding: "8px 10px", borderBottom: "0.5px solid var(--border)" }}>
                  <input className="fi" autoFocus placeholder="Search users…"
                    value={submitterSearch} onChange={(e) => setSubmitterSearch(e.target.value)}
                    style={{ fontSize: 12, padding: "4px 8px" }} />
                </div>
                {users.length === 0 ? (
                  <div style={{ padding: 10, fontSize: 12, color: "var(--text2)" }}><Spinner size={12} /></div>
                ) : (
                  users
                    .filter((u) => {
                      if (!submitterSearch) return true;
                      const q = submitterSearch.toLowerCase();
                      return (u.displayName || "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                    })
                    .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email))
                    .slice(0, 30)
                    .map((u) => (
                      <div key={u.uid} onClick={() => handleChangeSubmitter(u)}
                        style={{
                          padding: "8px 12px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 8,
                          background: u.uid === ticket.submitterId ? "var(--blue-l)" : "transparent",
                        }}
                        onMouseEnter={(e) => { if (u.uid !== ticket.submitterId) e.currentTarget.style.background = "var(--gray)"; }}
                        onMouseLeave={(e) => { if (u.uid !== ticket.submitterId) e.currentTarget.style.background = "transparent"; }}>
                        <Avatar name={u.displayName || u.email} size={20} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {u.displayName || u.email}
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {u.email}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, color: "var(--text3)" }}>{u.role}</span>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
          <div ref={assignRef} style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
            <span style={{ color: "var(--text2)" }}>Assignee</span>
            {isAgent ? (
              <button
                onClick={() => setShowAssign(!showAssign)}
                style={{ background: "var(--gray)", border: "none", padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "var(--font)" }}
              >
                {ticket.assignedToName ?? "Unassigned"} ▾
              </button>
            ) : (
              <span style={{ fontWeight: 500 }}>{ticket.assignedToName ?? "Unassigned"}</span>
            )}
            {showAssign && (
              <div className="card" style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, width: 200,
                maxHeight: 280, overflow: "auto", boxShadow: "var(--shadow-md)", zIndex: 30,
              }}>
                <div onClick={() => handleAssign(null)}
                  style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, color: "var(--text2)", borderBottom: "0.5px solid var(--border)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  Unassigned
                </div>
                {agents.length === 0 ? (
                  <div style={{ padding: 10, fontSize: 12, color: "var(--text2)" }}><Spinner size={12} /></div>
                ) : (
                  agents.map((a) => (
                    <div key={a.uid} onClick={() => handleAssign(a)}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <Avatar name={a.displayName} size={20} />
                      <span style={{ flex: 1 }}>{a.displayName}</span>
                      <span style={{ fontSize: 10, color: "var(--text3)" }}>{a.role}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {ticket.status === "pending_user" ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
                <span style={{ color: "var(--text2)" }}>SLA</span>
                <span style={{ fontWeight: 500, color: "var(--orange-t)" }}>Paused — waiting for user</span>
              </div>
              {(ticket.pendingUserRoundCount ?? 0) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
                  <span style={{ color: "var(--text2)" }}>Info rounds</span>
                  {/* The limit is stamped onto the ticket when the AI asks, so
                      submitters (who can't read integrations/ai) still see the
                      right denominator, and it doesn't shift mid-conversation
                      if an admin changes the setting. */}
                  <span style={{ fontWeight: 500 }}>
                    {ticket.pendingUserRoundCount} of {ticket.pendingUserMaxRounds ?? DEFAULT_MAX_INFO_ROUNDS}
                  </span>
                </div>
              )}
              {isAgent && (
                <button className="btn btn-primary btn-sm" style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
                  onClick={async () => {
                    if (!id) return;
                    try {
                      const fn = httpsCallable(getFunctions(), "forceOpenTicket");
                      await fn({ ticketId: id });
                      toast("Ticket advanced to triage");
                    } catch (e) {
                      toast(`Failed: ${(e as Error).message}`, "error");
                    }
                  }}>
                  Mark Ready for Triage
                </button>
              )}
            </>
          ) : slaRemainingMs !== null && (() => {
            // Build a screen-reader-friendly aria-label so "1924m" doesn't
            // read as "one nine two four m" in plain text.
            const abs = Math.abs(slaRemainingMs);
            const totalMin = Math.floor(abs / 60_000);
            const h = Math.floor(totalMin / 60);
            const m = totalMin % 60;
            const spoken = h > 0
              ? `${h} hour${h === 1 ? "" : "s"}${m > 0 ? ` ${m} minute${m === 1 ? "" : "s"}` : ""}`
              : `${m} minute${m === 1 ? "" : "s"}`;
            const ariaLabel = breached
              ? `SLA breached ${spoken} ago`
              : `SLA: ${spoken} remaining${schedule?.enabled ? " of business time" : ""}`;
            return (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, fontSize: 12 }}>
                <span style={{ color: "var(--text2)" }}>
                  SLA{schedule?.enabled ? " (business)" : ""}
                </span>
                <span aria-label={ariaLabel} style={{ fontWeight: 500, color: breached ? "var(--red-t)" : slaWarning ? "var(--orange-t)" : "var(--green-t)" }}>
                  {breached
                    ? `Breached ${formatSlaDuration(slaRemainingMs)} ago`
                    : `${formatSlaDuration(slaRemainingMs)} left`}
                </span>
              </div>
            );
          })()}
        </div>

        {/* CSAT — visible after submitter rates the resolution */}
        {ticket.csatScore !== undefined && (
          <div style={{ padding: 12, borderBottom: "0.5px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 8 }}>CSAT</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>
                {(["😡","🙁","😐","🙂","🤩"])[ticket.csatScore - 1] || "—"}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{ticket.csatScore}/5</span>
            </div>
            {ticket.csatComment && (
              <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.45, padding: "8px 10px", background: "var(--gray)", borderRadius: 6, fontStyle: "italic" }}>
                "{ticket.csatComment}"
              </div>
            )}
          </div>
        )}
        {/* CSAT — visible while a survey is outstanding */}
        {ticket.csatScore === undefined && ticket.csatSurveySentAt && ticket.status === "resolved" && (
          <div style={{ padding: 12, borderBottom: "0.5px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 6 }}>CSAT</div>
            <div style={{ fontSize: 11, color: "var(--text2)" }}>Survey sent — awaiting response.</div>
          </div>
        )}

        {/* AI analysis */}
        {ticket.triagedAt && (
          <div style={{ padding: 12, borderBottom: "0.5px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 8 }}>AI Analysis</div>
            <div style={{ fontSize: 11, background: "var(--gem-l)", color: "var(--gem-t)", padding: 8, borderRadius: 6, lineHeight: 1.5, marginBottom: 8 }}>
              Triaged in &lt;2s · Confidence: {ticket.triageConfidence ?? "—"}%
            </div>
            {ticket.triageReason && (
              <p style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5, marginBottom: 8 }}>{ticket.triageReason}</p>
            )}
            <button className="btn btn-gem" style={{ width: "100%", justifyContent: "center", fontSize: 12, padding: 8 }} onClick={() => navigate("/chat")}>
              Open AI Chat
            </button>
          </div>
        )}

        {/* Related KB article — only shown if the AI's suggested article
            actually exists in the knowledge base. Search uses ticket title +
            description so we can fall back to a topic match even when the
            AI's exact title doesn't exist. */}
        {(() => {
          if (kbArticles.length === 0) return null;
          const query = `${ticket.title} ${ticket.description}`;
          const matches = searchKBArticles(kbArticles, query, detectPlatform(), ticket.triageSuggestKB);
          const best = matches[0];
          if (!best) return null;
          return (
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 8 }}>Related KB Article</div>
              <div style={{ fontSize: 12, padding: 8, background: "var(--gray)", borderRadius: 6, cursor: "pointer" }}
                onClick={() => navigate(`/kb/${best.id}`)}>
                <div style={{ fontWeight: 500 }}>{best.title}</div>
                <div style={{ color: "var(--text2)", marginTop: 2 }}>View article →</div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function AttachmentChip({ att }: { att: TicketAttachment }) {
  const isImage = att.contentType.startsWith("image/");
  if (isImage) {
    return (
      <a href={att.url} target="_blank" rel="noopener noreferrer"
        style={{ display: "block", maxWidth: 200, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
        <img src={att.url} alt={att.name} style={{ display: "block", width: "100%", maxHeight: 160, objectFit: "cover" }} />
        <div style={{ padding: "4px 8px", fontSize: 11, color: "var(--text2)", background: "var(--surface)" }}>{att.name}</div>
      </a>
    );
  }
  return (
    <a href={att.url} target="_blank" rel="noopener noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "var(--gray)", borderRadius: 6, fontSize: 12, color: "var(--text)", textDecoration: "none", border: "0.5px solid var(--border)" }}>
      📎 {att.name} <span style={{ color: "var(--text3)", fontSize: 10 }}>{(att.size / 1024).toFixed(1)} KB</span>
    </a>
  );
}
